import React, { useState, useMemo, useEffect } from 'react';
import { calculateAdvancedStats, calculateAchievements, calculatePersona } from '../utils';
import DetailedDesignScreen from './DetailedDesignScreen';
import ShareResultsModal from './ShareResultsModal';
import IndividualPlayerStats from './IndividualPlayerStats';
import { getFirestore, collection, getDocs, query, orderBy, limit } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import CustomSelect from './CustomSelect';

const StatisticsScreen = ({ initialPlayers, initialHandHistory, gameConfig, onClose, gameState }) => {

    const [activeTab, setActiveTab] = useState('SUMMARY');
    const [selectedHandNumber, setSelectedHandNumber] = useState(1);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [completedGames, setCompletedGames] = useState([]);
    const [selectedGame, setSelectedGame] = useState(null);

    // State for current game view
    const [players, setPlayers] = useState(initialPlayers);
    const [handHistory, setHandHistory] = useState(initialHandHistory);

    const appId = "1:699575750232:web:8d7ebbfd7a7bb5c866842d";

    // Calculate winner for current game if finished
    // Calculate winners for current or selected game
    const calculatedWinners = useMemo(() => {
        const targetPlayers = selectedGame ? selectedGame.players : players;
        const targetState = selectedGame ? 'finished' : gameState;

        if (targetState === 'finished' && targetPlayers.length > 0) {
            const maxScore = Math.max(...targetPlayers.map(p => p.score));
            return targetPlayers.filter(p => p.score === maxScore);
        }
        return [];
    }, [gameState, players, selectedGame]);

    useEffect(() => {
        const auth = getAuth();
        const user = auth.currentUser;
        if (user) {
            const db = getFirestore();
            const getGames = async () => {
                const gamesCollection = collection(db, "personalData", appId, "users", user.uid, "games");
                const q = query(gamesCollection, orderBy("date", "desc"), limit(20));
                const gamesSnapshot = await getDocs(q);
                const gamesData = gamesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setCompletedGames(gamesData);
            };
            getGames();
        }
    }, [appId]);

    const handleGameSelection = (gameId) => {
        if (gameId === "") {
            setSelectedGame(null);
            setPlayers(initialPlayers);
            setHandHistory(initialHandHistory);
            return;
        }
        const game = completedGames.find(g => g.id === gameId);
        if (game) {
            setSelectedGame(game);
            setPlayers(game.players);
            setHandHistory(game.handHistory);
        }
    };

    const cumulativeStats = useMemo(() => {
        const stats = {};
        players.forEach(p => {
            stats[p.name] = { totalBags: 0, totalPenalties: 0 };
        });

        handHistory.forEach(hand => {
            Object.keys(hand.players).forEach(playerName => {
                const playerHandData = hand.players[playerName];
                const playerStats = stats[playerName];

                if (playerStats && playerHandData) {
                    const bid = Number(playerHandData.bid);
                    const won = playerHandData.won;

                    // Only count bags for non-zero bids
                    if (bid > 0) {
                        const bags = won - bid;
                        if (bags > 0) {
                            playerStats.totalBags += bags;
                        }
                    }
                }
            });
        });
        return stats;
    }, [handHistory, players]);

    const gameOptions = useMemo(() => {
        const options = [{ value: '', label: 'Current Game' }];
        completedGames.forEach(game => {
            options.push({
                value: game.id,
                label: new Date(game.date).toLocaleString()
            });
        });
        return options;
    }, [completedGames]);

    // Robustly calculate running totals for history (handles missing 'total' in old data)
    const processedHandHistory = useMemo(() => {
        if (!handHistory || handHistory.length === 0) return [];

        const runningTotals = {};
        players.forEach(p => runningTotals[p.name] = 0);

        return handHistory.map(hand => {
            const processedPlayers = {};
            // We need to iterate over all players to ensure we track their totals even if they didn't play a hand (unlikely in this game but good practice)
            players.forEach(player => {
                const pName = player.name;
                const pData = hand.players[pName];

                if (pData) {
                    if (pData.total !== undefined) {
                        runningTotals[pName] = pData.total;
                    } else {
                        runningTotals[pName] = (runningTotals[pName] || 0) + (pData.score || 0);
                    }

                    processedPlayers[pName] = {
                        ...pData,
                        total: runningTotals[pName]
                    };
                } else {
                    // Player might be missing from this hand (e.g. added later? unlikely)
                    processedPlayers[pName] = { total: runningTotals[pName] || 0 };
                }
            });
            return { ...hand, players: processedPlayers };
        });
    }, [handHistory, players]);

    // Calculate Analytics Data
    const analyticsData = useMemo(() => {
        // Performance Data (Line/Area Chart)
        const performance = [];
        for (let i = 0; i < processedHandHistory.length; i++) {
            const hand = processedHandHistory[i];
            const entry = { hand: `Hand ${i + 1}` };
            players.forEach(player => {
                entry[player.name] = hand.players[player.name]?.total || 0;
            });
            performance.push(entry);
        }

        // Bids vs Won Data (Bar Chart)
        const bidsVsWon = players.map(player => {
            let totalBids = 0;
            let totalWon = 0;
            handHistory.forEach(hand => {
                const playerData = hand.players[player.name];
                if (playerData) {
                    totalBids += Number(playerData.bid);
                    totalWon += playerData.won;
                }
            });
            return { name: player.name, Bids: totalBids, Won: totalWon };
        });

        // Bags Data (Bar Chart)
        const bags = players.map(player => {
            let totalBags = 0;
            handHistory.forEach(hand => {
                const playerData = hand.players[player.name];
                if (playerData) {
                    const bid = Number(playerData.bid);
                    if (bid > 0) {
                        const bags = playerData.won - bid;
                        if (bags > 0) {
                            totalBags += bags;
                        }
                    }
                }
            });
            return { name: player.name, Bags: totalBags };
        });

        return { performance, bidsVsWon, bags };
    }, [handHistory, processedHandHistory, players]);

    // Calculate Individual Player Stats
    const individualPlayerStats = useMemo(() => {
        return players.map(player => {
            let totalBids = 0;
            let bidHands = 0;
            let totalWon = 0;
            let totalBags = 0;
            const bidsVsWonData = handHistory.map((hand, index) => {
                const playerData = hand.players[player.name];
                if (playerData) {
                    const bid = Number(playerData.bid) || 0;
                    const won = playerData.won || 0;

                    totalBids += bid;
                    bidHands++;
                    totalWon += won;

                    // Track bags (overtricks)
                    if (won > bid) {
                        const bags = won - bid;
                        totalBags += bags;
                    }

                    return { hand: `H${index + 1}`, Bids: bid, Won: won };
                }
                return { hand: `H${index + 1}`, Bids: 0, Won: 0 };
            });

            // Calculate risk taking level (0-100)
            // Higher if they bid aggressively (bid close to or above average won)
            // Lower if they bid conservatively (bid well below what they typically win)
            let riskLevel = 50; // Default moderate risk
            if (bidHands > 0) {
                const avgBid = totalBids / bidHands;
                const avgWon = totalWon / bidHands;

                // If they win 0, but bid something, that's infinite risk (capped at 100)
                if (avgWon === 0) {
                    riskLevel = avgBid > 0 ? 100 : 0;
                } else {
                    const ratio = avgBid / avgWon;

                    // Ratio 1.0 (Bid = Won) -> 85% Risk (High efficiency/aggressive)
                    // Ratio 0.8 (Bid 80% of Won) -> 50% Risk (Moderate)
                    // Ratio 0.5 (Bid 50% of Won) -> 20% Risk (Conservative)
                    // Ratio > 1.0 (Bid > Won) -> 90-100% Risk (Overbidding)

                    if (ratio >= 1) {
                        // Linear scale from 1.0 (85%) to 1.2 (100%)
                        // Any ratio > 1.2 is capped at 100%
                        riskLevel = 85 + ((ratio - 1) / 0.2) * 15;
                    } else {
                        // Linear scale from 0.0 (0%) to 1.0 (85%)
                        riskLevel = ratio * 85;
                    }

                    riskLevel = Math.min(100, Math.max(0, Math.round(riskLevel)));
                }
            }

            const { bestHand, worstHand, maxStreak } = calculateAdvancedStats(handHistory, player.name);
            const achievements = calculateAchievements(handHistory, player.name);

            return {
                name: player.name,
                color: player.color,
                averageBid: bidHands > 0 ? (totalBids / bidHands).toFixed(2) : 0,
                averageTricksWon: handHistory.length > 0 ? (totalWon / handHistory.length).toFixed(2) : 0,
                averageBags: handHistory.length > 0 ? (totalBags / handHistory.length).toFixed(2) : 0,
                riskLevel: Math.round(riskLevel),
                bestHand,
                worstHand,
                maxStreak,
                achievements,
                bidsVsWonData,
            };
        });
    }, [players, handHistory]);

    return (
        <>
            <div className="h-screen bg-[#1a2332] font-['Inter'] text-[#f1f5f9] flex flex-col overflow-hidden relative">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-5 pointer-events-none">
                    <div className="absolute top-0 left-0 w-full h-full"
                        style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 5 C 35 15 45 15 45 25 C 45 35 30 50 30 50 C 30 50 15 35 15 25 C 15 15 25 15 30 5 Z' fill='%23ffffff' fill-rule='evenodd'/%3E%3C/svg%3E")`,
                            backgroundSize: '60px 60px'
                        }}
                    />
                </div>

                {/* Ambient Glow */}
                <div className="absolute top-0 left-1/4 w-[50vw] h-[50vw] bg-purple-500/10 rounded-full blur-[100px] -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
                <div className="absolute bottom-0 right-1/4 w-[50vw] h-[50vw] bg-blue-500/10 rounded-full blur-[100px] translate-x-1/2 translate-y-1/2 pointer-events-none" />

                <header className="sticky top-0 z-20 border-b border-white/5 bg-[#1a2332]/80 px-4 py-3 backdrop-blur-md flex-shrink-0 shadow-lg flex flex-col md:flex-row items-center gap-3 md:gap-0">
                    {/* Top Row (Mobile) / Left Section (Desktop) */}
                    <div className="w-full md:w-auto md:flex-1 flex items-center justify-between md:justify-start gap-4">
                        <button
                            onClick={onClose}
                            className="md:hidden group flex items-center justify-center w-10 h-10 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-amber-500/30 transition-all duration-200"
                            title="Close"
                        >
                            <svg className="w-5 h-5 text-gray-400 group-hover:text-amber-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        {/* Desktop Selector */}
                        <div className="hidden md:block w-48">
                            <CustomSelect
                                options={gameOptions}
                                value={selectedGame ? selectedGame.id : ''}
                                onChange={handleGameSelection}
                                placeholder="Select Game"
                            />
                        </div>

                        {/* Mobile Title */}
                        <h1 className="md:hidden text-xl font-black tracking-tight font-['Roboto_Slab'] text-transparent bg-clip-text bg-gradient-to-r from-amber-200 to-yellow-400">
                            STATISTICS
                        </h1>

                        {/* Mobile Actions */}
                        <div className="flex md:hidden items-center gap-2">
                            <button
                                onClick={() => setIsShareModalOpen(true)}
                                className="group flex items-center justify-center w-10 h-10 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-amber-500/30 transition-all duration-200"
                                title="Share"
                            >
                                <svg className="w-5 h-5 text-gray-400 group-hover:text-amber-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                            </button>
                        </div>
                    </div>

                    {/* Desktop Title (Centered) */}
                    <div className="hidden md:block flex-1 text-center">
                        <h1 className="text-3xl font-black tracking-tight font-['Roboto_Slab'] text-transparent bg-clip-text bg-gradient-to-r from-amber-200 to-yellow-400">
                            STATISTICS
                        </h1>
                    </div>

                    {/* Desktop Actions (Right) */}
                    <div className="hidden md:flex flex-1 items-center justify-end gap-2">
                        <button
                            onClick={() => setIsShareModalOpen(true)}
                            className="group flex items-center justify-center w-10 h-10 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-amber-500/30 transition-all duration-200"
                            title="Share"
                        >
                            <svg className="w-5 h-5 text-gray-400 group-hover:text-amber-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                        </button>
                        <button
                            onClick={onClose}
                            className="group flex items-center justify-center w-10 h-10 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-amber-500/30 transition-all duration-200"
                            title="Close"
                        >
                            <svg className="w-5 h-5 text-gray-400 group-hover:text-amber-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                    </div>

                    {/* Mobile Selector (Bottom Row) */}
                    <div className="w-full md:hidden">
                        <CustomSelect
                            options={gameOptions}
                            value={selectedGame ? selectedGame.id : ''}
                            onChange={handleGameSelection}
                            placeholder="Select Game"
                        />
                    </div>
                </header>

                <div className="flex-1 overflow-y-auto p-4 sm:p-6 pb-12 sm:pb-12 space-y-6">
                    {/* Tabs */}
                    <div className="flex justify-center">
                        <div className="bg-black/20 p-1 rounded-2xl border border-white/5 inline-flex relative">
                            {['SUMMARY', 'ANALYTICS', 'PLAYERS'].map((tab) => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`
                                        relative px-6 py-2.5 rounded-xl text-sm font-bold tracking-wider transition-all duration-300
                                        ${activeTab === tab
                                            ? 'bg-amber-500/10 text-amber-400 shadow-[0_0_20px_rgba(245,158,11,0.3)] border border-amber-500/20'
                                            : 'text-gray-400 hover:text-white hover:bg-white/5 border border-transparent'}
                                    `}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Content */}
                    <div className="max-w-7xl mx-auto">
                        {activeTab === 'SUMMARY' && (
                            <div className="space-y-6">
                                {/* Hero Section - Game MVP */}
                                {calculatedWinners.length > 0 ? (
                                    <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-amber-500/20 to-orange-600/20 border border-amber-500/30 p-6 sm:p-8">
                                        <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/20 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/2 pointer-events-none" />

                                        <div className="relative z-10 flex flex-col sm:flex-row items-center gap-6 sm:gap-8">
                                            <div className="relative">
                                                <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-gradient-to-br from-amber-400 to-orange-600 p-1 shadow-2xl shadow-amber-500/30">
                                                    <div className="w-full h-full rounded-full bg-[#1a2332] flex items-center justify-center text-4xl sm:text-5xl font-black text-white">
                                                        {calculatedWinners[0].name.charAt(0)}
                                                    </div>
                                                </div>
                                                <div className="absolute -bottom-2 -right-2 bg-amber-500 text-black text-xs font-black px-3 py-1 rounded-full border-2 border-[#1a2332]">
                                                    MVP
                                                </div>
                                            </div>

                                            <div className="text-center sm:text-left">
                                                <div className="text-amber-400 font-bold tracking-widest text-xs uppercase mb-2">Current Leader</div>
                                                <h2 className="text-4xl sm:text-5xl font-black text-white mb-2 tracking-tight">
                                                    {calculatedWinners[0].name}
                                                </h2>
                                                <div className="flex items-center justify-center sm:justify-start gap-4">
                                                    <div className="bg-black/30 px-4 py-2 rounded-xl border border-white/10 backdrop-blur-sm">
                                                        <span className="text-2xl font-black text-white">{calculatedWinners[0].score}</span>
                                                        <span className="text-xs text-gray-400 ml-2 font-bold uppercase">Points</span>
                                                    </div>
                                                    {players.length > 1 && (
                                                        <div className="text-sm font-medium text-gray-400">
                                                            Leading by <span className="text-emerald-400 font-bold">
                                                                {calculatedWinners[0].score - [...players].sort((a, b) => b.score - a.score)[1].score}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center p-8 text-gray-500">Game in progress...</div>
                                )}

                                {/* Game Pulse - Mini Trend */}
                                <div className="bg-[#1e293b]/50 backdrop-blur-md rounded-3xl border border-white/10 p-6 shadow-xl">
                                    <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-6 flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse"></span>
                                        Game Pulse
                                    </h3>
                                    <div className="h-32 w-full flex gap-1">
                                        {processedHandHistory.map((hand, idx) => {
                                            const scores = Object.values(hand.players).map(p => p.total || 0);
                                            const leaderScore = Math.max(...scores);
                                            // Normalize height: 
                                            // If leaderScore <= 0 (start of game or negative), show min height.
                                            // Scale based on 500 points (game end) or current max if higher.
                                            const maxScale = Math.max(500, leaderScore);
                                            const height = Math.max(10, Math.min(100, (leaderScore / maxScale) * 100));

                                            return (
                                                <div key={idx} className="flex-1 h-full flex flex-col justify-end group relative border-r border-white/5 last:border-0">
                                                    <div
                                                        className="w-full bg-gradient-to-t from-blue-500/60 to-blue-400/80 rounded-t-sm transition-all hover:from-blue-500/80 hover:to-blue-400/90"
                                                        style={{ height: `${height}%` }}
                                                    />
                                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity bg-black/80 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap pointer-events-none z-10 border border-white/10">
                                                        Hand {idx + 1}: {leaderScore}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        {processedHandHistory.length === 0 && (
                                            <div className="w-full h-full flex items-center justify-center text-gray-600 text-sm italic">
                                                No hands played yet
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Player Personas Grid */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {individualPlayerStats.map((stats, idx) => {
                                        const persona = calculatePersona(stats); // Assuming imported from utils
                                        // We need to import calculatePersona in StatisticsScreen.js
                                        // For now, I'll assume it's available or I'll add the import in the next step if needed.
                                        // Wait, I can't easily add the import in this same step if I'm only replacing this block.
                                        // I will assume calculatePersona is available or I will add it to the imports in a separate step if it fails.
                                        // Actually, I should check imports first.
                                        // Let's proceed, I'll fix imports if needed.

                                        return (
                                            <div key={idx} className="bg-[#1e293b]/50 backdrop-blur-md rounded-2xl border border-white/10 p-4 flex items-center gap-4 hover:bg-white/5 transition-colors">
                                                <div className="w-12 h-12 rounded-full bg-black/30 flex items-center justify-center text-2xl">
                                                    {persona.icon}
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-bold text-white">{stats.name}</span>
                                                        <span className={`text-[10px] font-black uppercase px-1.5 py-0.5 rounded bg-white/5 ${persona.color}`}>
                                                            {persona.name}
                                                        </span>
                                                    </div>
                                                    <div className="text-xs text-gray-400 mt-0.5">
                                                        {persona.desc}
                                                    </div>
                                                </div>
                                                <div className="ml-auto text-right">
                                                    <div className="text-lg font-black text-white">#{idx + 1}</div>
                                                    <div className="text-[10px] text-gray-500 font-bold uppercase">Rank</div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {activeTab === 'ANALYTICS' && (
                            <DetailedDesignScreen
                                players={players}
                                analyticsData={analyticsData}
                                gameConfig={gameConfig}
                            />
                        )}

                        {activeTab === 'PLAYERS' && (
                            <IndividualPlayerStats playerStats={individualPlayerStats} />
                        )}
                    </div>
                </div>

                <ShareResultsModal
                    isOpen={isShareModalOpen}
                    onClose={() => setIsShareModalOpen(false)}
                    players={players}
                    winner={calculatedWinners.length === 1 ? calculatedWinners[0] : null}
                />
            </div>
        </>
    );
};

export default StatisticsScreen;